<script setup lang="ts">
import { ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@modules/auth'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import AppSidebar from './AppSidebar.vue'
import AppTopNav from './AppTopNav.vue'
import AppFooter from './AppFooter.vue'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const mobileSidebarOpen = ref(false)
const sidebarCollapsed = ref(false)

const handleLogout = () => {
  authStore.logout()
  router.push(routes.LOGIN)
}

const toggleMobileSidebar = () => {
  mobileSidebarOpen.value = !mobileSidebarOpen.value
}

const closeMobileSidebar = () => {
  mobileSidebarOpen.value = false
}

const toggleSidebarCollapse = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

watch(
  () => route.fullPath,
  () => {
    mobileSidebarOpen.value = false
  },
)

// The shell scrolls inside .page-content, so the router's scrollBehavior never
// resets it — a new page opened at the previous page's offset (OE-20). Keyed on
// path, not fullPath, so query-only changes (tabs, filters) keep the position.
const pageContentRef = ref<InstanceType<typeof Box> | null>(null)
watch(
  () => route.path,
  () => {
    const el: unknown = pageContentRef.value?.$el
    if (el instanceof HTMLElement) el.scrollTop = 0
  },
)
</script>

<template>
  <Box class="app-shell">
    <Box
      v-if="mobileSidebarOpen"
      class="sidebar-overlay"
      aria-hidden="true"
      @click="closeMobileSidebar"
    />

    <AppSidebar
      :mobile-open="mobileSidebarOpen"
      :on-close-mobile="closeMobileSidebar"
      :collapsed="sidebarCollapsed"
    />

    <Box class="main-area">
      <AppTopNav
        :sidebar-collapsed="sidebarCollapsed"
        @toggle-sidebar="toggleMobileSidebar"
        @toggle-collapse="toggleSidebarCollapse"
        @logout="handleLogout"
      />

      <Box ref="pageContentRef" is="main" class="page-content">
        <RouterView />
      </Box>

      <AppFooter />
    </Box>
  </Box>
</template>

<style scoped>
.app-shell {
  display: flex;
  height: 100dvh;
  overflow: hidden;
  position: relative;
}

.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.page-content::-webkit-scrollbar {
  display: none;
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  z-index: 35;
  background: rgba(10, 10, 10, 0.45);
}

@media (max-width: 991px) {
  .main-area {
    width: 100%;
  }

  .page-content {
    padding: 1rem;
  }
}
</style>
